import { DomainError } from '../errors/domain-error';

export const COMMUNITY_POST_RATE_LIMIT_DEFAULT = 20;
export const COMMUNITY_POST_RATE_LIMIT_MAX = 120;
export const COMMUNITY_POST_SLOW_MODE_MAX_SECONDS = 3600;
export const COMMUNITY_BLOCKED_WORDS_MAX = 50;
export const COMMUNITY_BLOCKED_WORD_MAX_LENGTH = 40;

export type CommunitySpamViolationCode = 'blocked_word' | 'rate_limit' | 'slow_mode';

export function normalizeBlockedWords(words: string[] | null | undefined): string[] {
  if (!words?.length) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of words) {
    const word = raw.trim().toLowerCase();
    if (!word || word.length > COMMUNITY_BLOCKED_WORD_MAX_LENGTH) continue;
    if (seen.has(word)) continue;
    seen.add(word);
    normalized.push(word);
    if (normalized.length >= COMMUNITY_BLOCKED_WORDS_MAX) break;
  }
  return normalized;
}

export function findBlockedWord(content: string, blockedWords: string[]): string | null {
  const haystack = content.trim().toLowerCase();
  if (!haystack || blockedWords.length === 0) return null;
  for (const word of blockedWords) {
    if (haystack.includes(word)) return word;
  }
  return null;
}

export function validateCommunityPostSpam(params: {
  content: string;
  blockedWords: string[];
  postsLastHour: number;
  rateLimitPerHour: number | null | undefined;
  lastPostAt: Date | string | null | undefined;
  slowModeSeconds: number | null | undefined;
  now?: Date;
}): { ok: true } | { ok: false; code: CommunitySpamViolationCode; message: string } {
  const blocked = findBlockedWord(params.content, params.blockedWords);
  if (blocked) {
    return {
      ok: false,
      code: 'blocked_word',
      message: 'Post contains blocked language',
    };
  }

  const rateLimit = params.rateLimitPerHour ?? COMMUNITY_POST_RATE_LIMIT_DEFAULT;
  if (rateLimit > 0 && params.postsLastHour >= rateLimit) {
    return {
      ok: false,
      code: 'rate_limit',
      message: 'Post rate limit reached. Try again later.',
    };
  }

  const slowModeSeconds = params.slowModeSeconds ?? 0;
  if (slowModeSeconds > 0 && params.lastPostAt) {
    const now = params.now ?? new Date();
    const lastPostAt =
      params.lastPostAt instanceof Date ? params.lastPostAt : new Date(params.lastPostAt);
    const elapsedMs = now.getTime() - lastPostAt.getTime();
    if (elapsedMs < slowModeSeconds * 1000) {
      return {
        ok: false,
        code: 'slow_mode',
        message: `Slow mode is active. Wait ${slowModeSeconds} seconds between posts.`,
      };
    }
  }

  return { ok: true };
}

export function assertCommunityPostSpam(
  params: Parameters<typeof validateCommunityPostSpam>[0],
): void {
  const result = validateCommunityPostSpam(params);
  if (!result.ok) {
    throw new DomainError(result.message);
  }
}
